import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ReplaySubject } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { AppConsts } from '@shared/AppConsts';
import {
  DEFAULT_DETECTION_RESULT,
  RegionalDetectionResult,
  standardCodeForCountry
} from './regional-standard.model';

/**
 * Resolves the visitor's regional cable sizing standard from their IP, once per browser session
 * (CPW-5547).
 *
 * The result is cached in sessionStorage rather than only in this singleton, because on the public
 * site switching standards navigates via `location.href` (see
 * WebfacingHeaderV2Component.onStandardChange) - a full page reload that destroys all in-memory
 * state. sessionStorage is what actually delivers "one lookup per session"; it is also per-origin
 * rather than per-path, so it survives across every /calculators/* page.
 *
 * Deliberately session-scoped, never localStorage: CPW-5547 requires the detection to be forgotten
 * when the browser session ends.
 */
@Injectable({ providedIn: 'root' })
export class RegionalStandardService {

  private static readonly KEY_RESULT = 'webfacing.regionalstandard.detection';
  private static readonly KEY_POPUP_SHOWN = 'webfacing.regionalstandard.popupshown';

  /** How long to wait for the lookup before falling back to the site default. */
  private static readonly LOOKUP_TIMEOUT_MS = 4000;

  /**
   * Emits exactly once per page load - from the session cache or from the network.
   * ReplaySubject(1) so late subscribers (the header, which may construct after detection has
   * already settled) still receive the result.
   */
  readonly detection$ = new ReplaySubject<RegionalDetectionResult>(1);

  private started = false;
  private settled = false;

  constructor(private http: HttpClient) { }

  /**
   * Kicks off detection if it has not already run. Safe and cheap to call on every navigation.
   */
  ensureDetected(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    const cached = this.readFromSession();
    if (cached) {
      this.settle(cached);
      return;
    }

    // X-Skip-Interceptor: this is a background enhancement. Without it a transient failure would
    // raise the global "Network Unreachable" modal over a perfectly usable calculator page.
    // Note the interceptor answers a skipped failure with of() - an empty observable that
    // completes WITHOUT emitting - which is why `complete` below must settle to the default.
    const headers = new HttpHeaders({ 'X-Skip-Interceptor': 'true' });

    this.http
      .get<any>(`${AppConsts.remoteServiceBaseUrl}/api/services/app/CommonLookup/GetIpCountry`,
        { headers, withCredentials: true })
      .pipe(timeout(RegionalStandardService.LOOKUP_TIMEOUT_MS))
      .subscribe({
        next: res => this.settle(this.toResult(res?.result)),
        error: () => this.settle(DEFAULT_DETECTION_RESULT),
        complete: () => this.settle(DEFAULT_DETECTION_RESULT)
      });
  }

  /** True once the popup has been shown or dismissed in this session. */
  hasPopupBeenShown(): boolean {
    try {
      return sessionStorage.getItem(RegionalStandardService.KEY_POPUP_SHOWN) === 'true';
    } catch (e) {
      console.warn('Failed to read popup flag from sessionStorage:', e);
      return false;
    }
  }

  /**
   * Records that the popup has been shown. Call this when OPENING the modal, not when closing it:
   * CPW-5547 says "once shown or dismissed", and a user who navigates away with the modal open
   * would otherwise see it again on the next page.
   */
  markPopupShown(): void {
    try {
      sessionStorage.setItem(RegionalStandardService.KEY_POPUP_SHOWN, 'true');
    } catch (e) {
      console.warn('Failed to persist popup flag to sessionStorage:', e);
    }
  }

  private toResult(payload: any): RegionalDetectionResult {
    if (!payload?.detected) {
      return DEFAULT_DETECTION_RESULT;
    }

    const standardCode = standardCodeForCountry(payload.countryCode);
    if (!standardCode) {
      // Country resolved but not in the mapping table - site default, and no popup.
      return DEFAULT_DETECTION_RESULT;
    }

    return {
      status: 'detected',
      countryCode: payload.countryCode,
      countryName: payload.countryName,
      standardCode
    };
  }

  /**
   * Caches the result for the session and emits it. Failures are cached too: CPW-5547 wants one
   * lookup per session, and re-requesting on every navigation while the API is down would defeat
   * that for no benefit.
   */
  private settle(result: RegionalDetectionResult): void {
    if (this.settled) {
      return;
    }
    this.settled = true;

    this.writeToSession(result);
    this.detection$.next(result);
  }

  private readFromSession(): RegionalDetectionResult | null {
    try {
      const raw = sessionStorage.getItem(RegionalStandardService.KEY_RESULT);
      return raw ? JSON.parse(raw) as RegionalDetectionResult : null;
    } catch (e) {
      console.warn('Failed to read detection from sessionStorage:', e);
      return null;
    }
  }

  private writeToSession(result: RegionalDetectionResult): void {
    try {
      sessionStorage.setItem(RegionalStandardService.KEY_RESULT, JSON.stringify(result));
    } catch (e) {
      console.warn('Failed to persist detection to sessionStorage:', e);
    }
  }
}
